import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, X, Sparkles, Instagram, Facebook, Music2, MessageCircle } from "lucide-react";
import { api, inputCls, labelCls } from "@/lib/api";

const ACTION_LABELS = {
  request_appointment: "Request Appointment",
  ask_about_service: "Ask About a Service",
  external_link: "Book Now",
  message_on_whatsapp: "Message on WhatsApp",
};

const priceLabel = (p) => (p ? `From ${/^\d/.test(p) ? `$${p}` : p}` : "");

const PublicPage = () => {
  const { slug } = useParams();
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);
  const [enquiry, setEnquiry] = useState(null);
  const [showAllServices, setShowAllServices] = useState(false);
  const tracked = useRef(false);

  useEffect(() => {
    api(`/public/${slug}`)
      .then((d) => {
        setData(d);
        setState("ok");
        if (!tracked.current) {
          tracked.current = true;
          api(`/public/${slug}/track`, { method: "POST", body: JSON.stringify({ type: "page_visit" }) }).catch(() => {});
        }
      })
      .catch(() => setState("notfound"));
  }, [slug]);

  if (state === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F7F7F5]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-guinep-line border-t-guinep-deep" />
      </div>
    );
  }

  if (state === "notfound") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#F7F7F5] px-5 text-center font-sans" data-testid="page-not-found">
        <h1 className="font-serif text-3xl font-medium text-guinep-ink">Page not found</h1>
        <p className="mt-2 max-w-xs text-sm text-guinep-mute">This Guinep Seed page doesn't exist or isn't live right now.</p>
        <Link to="/" data-testid="notfound-home-link" className="mt-6 rounded-full bg-guinep-deep px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-guinep-mid">
          Go to Guinep Seed
        </Link>
      </div>
    );
  }

  const { business: b, services, now, has_whatsapp } = data;
  const actionLabel = ACTION_LABELS[b.action_type] || ACTION_LABELS.request_appointment;

  const openPrimary = (service) => {
    if (b.action_type === "external_link" && b.external_link) {
      window.open(b.external_link, "_blank", "noopener");
      return;
    }
    if (b.action_type === "message_on_whatsapp") {
      sendEnquiry({ service_id: null, service_title: "General enquiry", preferred_date: "", client_type: "new", note: "" });
      return;
    }
    if (service?.service_id) {
      api(`/public/${slug}/track`, { method: "POST", body: JSON.stringify({ type: "service_view", service_id: service.service_id }) }).catch(() => {});
    }
    setEnquiry(service || null);
  };

  const sendEnquiry = async (payload) => {
    const res = await api(`/public/${slug}/enquiry`, { method: "POST", body: JSON.stringify(payload) });
    if (res.whatsapp_url) {
      window.open(res.whatsapp_url, "_blank", "noopener");
    } else {
      throw new Error("This business hasn't connected WhatsApp yet — please try their social links.");
    }
  };

  const policies = b.policies_display || [];
  const visibleServices = showAllServices ? services : services.slice(0, 6);

  return (
    <div className="min-h-screen bg-[#F7F7F5] pb-28 font-sans" data-testid="public-page">
      <div className="mx-auto max-w-md px-5 pt-12">
        <div className="flex flex-col items-center text-center">
          <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl bg-white font-serif text-2xl font-medium text-guinep-deep shadow-sm" data-testid="public-avatar">
            {b.photo ? (
              <img src={`/api/files/${b.photo}`} alt={b.name} className="h-full w-full object-cover" />
            ) : (
              b.name.charAt(0)
            )}
          </div>
          <h1 className="mt-4 font-serif text-3xl font-medium tracking-tight text-guinep-ink" data-testid="public-business-name">{b.name}</h1>
          {b.description && <p className="mt-1.5 max-w-xs text-sm text-guinep-mute" data-testid="public-description">{b.description}</p>}
          {b.location && (
            <p className="mt-1.5 flex items-center gap-1 text-xs text-guinep-mute" data-testid="public-location">
              <MapPin size={12} /> {b.location}
            </p>
          )}
          {(b.instagram || b.tiktok || b.facebook) && (
            <div className="mt-3 flex gap-3" data-testid="public-socials">
              {b.instagram && <a href={`https://instagram.com/${b.instagram}`} target="_blank" rel="noreferrer" data-testid="social-instagram" className="text-guinep-mute transition-colors hover:text-guinep-deep"><Instagram size={18} /></a>}
              {b.tiktok && <a href={`https://tiktok.com/@${b.tiktok}`} target="_blank" rel="noreferrer" data-testid="social-tiktok" className="text-guinep-mute transition-colors hover:text-guinep-deep"><Music2 size={18} /></a>}
              {b.facebook && <a href={b.facebook} target="_blank" rel="noreferrer" data-testid="social-facebook" className="text-guinep-mute transition-colors hover:text-guinep-deep"><Facebook size={18} /></a>}
            </div>
          )}
        </div>

        {now && (
          <div className="mt-7 rounded-2xl border border-guinep-line/70 bg-guinep-soft p-4" data-testid="public-now-card">
            <div className="flex items-center gap-2">
              <Sparkles size={13} className="text-guinep-mid" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-guinep-mid">{now.type_label}</span>
            </div>
            <p className="mt-1.5 font-serif text-base text-guinep-ink">{now.message}</p>
            <button
              data-testid="now-cta-button"
              onClick={() => openPrimary(null)}
              className="mt-3 rounded-lg bg-guinep-deep px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-guinep-mid"
            >
              {actionLabel}
            </button>
          </div>
        )}

        {services.length > 0 && (
          <div className="mt-8">
            <h2 className="font-serif text-lg font-medium text-guinep-ink">Services</h2>
            <div className="mt-3 space-y-3">
              {visibleServices.map((s) => (
                <div key={s.service_id} className="rounded-2xl border border-guinep-line/70 bg-white p-4" data-testid="public-service-card">
                  <p className="text-sm font-semibold text-guinep-ink">{s.title}</p>
                  <p className="mt-0.5 text-xs text-guinep-mute">
                    {[priceLabel(s.price), s.duration, s.consultation_required ? "Consultation required" : ""].filter(Boolean).join(" · ")}
                  </p>
                  <button
                    data-testid={`interested-${s.service_id}`}
                    onClick={() => openPrimary(s)}
                    className="mt-3 w-full rounded-xl border border-guinep-line py-2.5 text-[13px] font-semibold text-guinep-ink transition-colors hover:border-guinep-mid hover:bg-guinep-soft"
                  >
                    I'm Interested
                  </button>
                </div>
              ))}
              {services.length > 6 && !showAllServices && (
                <button
                  data-testid="view-more-services"
                  onClick={() => setShowAllServices(true)}
                  className="w-full rounded-xl border border-dashed border-guinep-line py-3 text-[13px] font-semibold text-guinep-mid transition-colors hover:border-guinep-mid hover:bg-guinep-soft"
                >
                  View more services
                </button>
              )}
            </div>
          </div>
        )}

        {policies.length > 0 && (
          <div className="mt-8">
            <h2 className="font-serif text-lg font-medium text-guinep-ink">Before you book</h2>
            <div className="mt-3 space-y-2.5" data-testid="public-policies">
              {policies.map((p) => (
                <div key={p.key} className="rounded-2xl border border-guinep-line/70 bg-white p-4">
                  <p className="text-[13px] font-semibold text-guinep-ink">{p.label}</p>
                  <p className="mt-1 text-[13px] leading-relaxed text-guinep-mute">{p.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="mt-12 text-center text-[11px] text-guinep-mute/50">
          Powered by <Link to="/" className="font-semibold text-guinep-mute/70 hover:text-guinep-deep">Guinep Seed</Link>
        </p>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 bg-gradient-to-t from-[#F7F7F5] via-[#F7F7F5] to-transparent px-5 pb-5 pt-8">
        <div className="mx-auto max-w-md">
          <button
            data-testid="sticky-whatsapp-cta"
            onClick={() => openPrimary(null)}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-wa py-4 text-sm font-semibold text-white shadow-[0_12px_28px_-8px_rgba(37,211,102,0.5)] transition-colors hover:bg-wa-dark"
          >
            <MessageCircle size={17} /> {actionLabel} on WhatsApp
          </button>
        </div>
      </div>

      {enquiry !== null && (
        <EnquiryModal
          business={b}
          services={services}
          initialService={enquiry?.service_id ? enquiry : null}
          hasWhatsapp={has_whatsapp}
          onClose={() => setEnquiry(null)}
          onSend={sendEnquiry}
        />
      )}
    </div>
  );
};

export const EnquiryModal = ({ business, services, initialService, hasWhatsapp, onClose, onSend }) => {
  const [serviceId, setServiceId] = useState(initialService?.service_id || "");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [clientType, setClientType] = useState("new");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const selected = services.find((s) => s.service_id === serviceId);
  const title = selected ? `Enquire about ${selected.title}` : `Message ${business.name}`;

  const submit = async () => {
    if (date || time) {
      const digits = phone.replace(/\D/g, "").replace(/^0+/, "");
      const valid = digits.length === 7 || (digits.length >= 10 && digits.length <= 15);
      if (!valid) {
        setError("Please enter a valid WhatsApp number so the business can confirm your appointment.");
        return;
      }
    }
    setBusy(true);
    setError("");
    try {
      await onSend({
        service_id: selected?.service_id || null,
        service_title: selected?.title || "General enquiry",
        preferred_date: date,
        preferred_time: time,
        client_name: name,
        client_phone: phone,
        client_type: clientType,
        note,
      });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-5" data-testid="enquiry-modal">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-xl font-medium text-guinep-ink" data-testid="enquiry-title">{title}</h3>
          <button onClick={onClose} data-testid="enquiry-close" className="text-guinep-mute hover:text-guinep-ink"><X size={18} /></button>
        </div>
        <div className="mt-5 space-y-4">
          <div>
            <label className={labelCls}>Service</label>
            <select data-testid="enquiry-service" className={inputCls} value={serviceId} onChange={(e) => setServiceId(e.target.value)}>
              <option value="">General enquiry</option>
              {services.map((s) => <option key={s.service_id} value={s.service_id}>{s.title}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Preferred date</label>
            <input type="date" data-testid="enquiry-date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Preferred time</label>
            <input type="time" data-testid="enquiry-time" className={inputCls} value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>WhatsApp number *</label>
            <input data-testid="enquiry-phone" className={inputCls} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="e.g. +1 268 555 0142" inputMode="tel" />
            <p className="mt-1 text-xs text-guinep-mute">We'll use this so the business can confirm your appointment.</p>
          </div>
          <div>
            <label className={labelCls}>Your name <span className="font-normal text-guinep-mute/60">(optional)</span></label>
            <input data-testid="enquiry-name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Jasmine" />
          </div>
          <div>
            <label className={labelCls}>Are you a new or existing client?</label>
            <div className="flex gap-2.5">
              {[["new", "New client"], ["existing", "Existing client"]].map(([v, l]) => (
                <button
                  key={v}
                  data-testid={`enquiry-client-${v}`}
                  onClick={() => setClientType(v)}
                  className={`flex-1 rounded-xl border py-2.5 text-[13px] font-semibold transition-colors ${clientType === v ? "border-guinep-deep bg-guinep-soft text-guinep-deep" : "border-guinep-line text-guinep-mute hover:border-guinep-mid/50"}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={labelCls}>Note (optional)</label>
            <textarea data-testid="enquiry-note" className={`${inputCls} min-h-[64px] resize-none`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Shoulder-length locs" />
          </div>
          {error && <p data-testid="enquiry-error" className="text-sm text-red-600">{error}</p>}
          <button
            data-testid="enquiry-submit"
            onClick={submit}
            disabled={busy || !hasWhatsapp}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-wa py-3.5 text-sm font-semibold text-white transition-colors hover:bg-wa-dark disabled:opacity-50"
          >
            <MessageCircle size={16} /> {busy ? "Opening WhatsApp…" : "Continue to WhatsApp"}
          </button>
          {!hasWhatsapp && <p className="text-center text-xs text-guinep-mute">This business hasn't connected WhatsApp yet.</p>}
        </div>
      </div>
    </div>
  );
};

export default PublicPage;
